// packages/report-components/src/ChartPaletteContext.tsx
//
// The seam where a surface tells the charts which palette the report draws
// with (plan 2026-09-26-report-charts-follow-the-account-palette): the
// backend resolves report override > account > platform and sends
// `chart_palette`; every surface mounts this provider with it. A sibling of
// BrandContext, not a field of the brand: the brand is composed-only, the
// palette reaches every report, JSX/template ones included.
//
// The provider is the SINK half of the hex validation (the producer half is
// the backend's `HexColour`). Palette strings end up as SVG `fill`/`stroke`
// in the static edition, where `url(...)` would be a paint reference that
// makes the renderer or a mail client fetch a URL — so only `#rrggbb`
// passes, and fewer than two survivors is "no palette" (the token fallback,
// silently; the backend already logged it).
//
// Plain `string[]`, declared here, for the same one-workspace-dependency
// reason as `ReportBrandTokens` (see the header of `brand.ts`).

import * as React from "react";

import { isHexColour } from "./brand";

const ChartPaletteContext = React.createContext<string[] | null>(null);

/** The palette with every non-`#rrggbb` entry dropped; null when fewer than
 * two remain, or when there was none. */
export function sanitizeChartPalette(palette: readonly unknown[] | null | undefined): string[] | null {
  if (!Array.isArray(palette)) return null;
  const valid = palette.filter(isHexColour);
  return valid.length >= 2 ? valid : null;
}

export type ReportChartPaletteProviderProps = {
  palette: readonly string[] | null | undefined;
  children: React.ReactNode;
};

export function ReportChartPaletteProvider({
  palette,
  children,
}: ReportChartPaletteProviderProps): React.ReactElement {
  const value = React.useMemo(() => sanitizeChartPalette(palette), [palette]);
  return <ChartPaletteContext.Provider value={value}>{children}</ChartPaletteContext.Provider>;
}

/** The report's chart palette (sanitised), or null on a surface that mounted
 * no provider or received none — the charts then use the token palette. */
export function useReportChartPalette(): string[] | null {
  return React.useContext(ChartPaletteContext);
}
